import React, { FunctionComponent } from "react";
import { connect, useSelector } from "react-redux";
import { Grid, Hidden } from "@mui/material";

import { AppState } from "@store";
import { IDetailsLanguage } from "@ducks/config/types";
import { IItem, REDEEMTYPE } from "@ducks/listing/types";
import { isReward, checkAndSetQueryParam } from "@lib/common";
import { getShortMonthName } from "@lib/datetime";
import { trackLink } from "@analytics";

interface IAttributesProps {
  details: IItem;
  language: IDetailsLanguage;
}

const Attributes: FunctionComponent<IAttributesProps> = (props) => {
  const { details: d, language: l } = props;
  const isApp = useSelector((state: AppState) => state.config.app.isApp);
  const appQueryParam = { key: "app", val: "1" };
  const keys: React.ReactNode[] = [];
  const values: React.ReactNode[] = [];

  const _flashSalePriceText = (price: string): string => {
    return `${l.RM} ${parseFloat(price).toFixed(2)}`;
  };

  const _periodText = (
    fromDateStr: string,
    toDateStr: string
  ): string | null => {
    const from = new Date(fromDateStr);
    const to = new Date(toDateStr);
    if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
      const fromYear = from.getFullYear();
      const toYear = to.getFullYear();
      const showFromYear = fromYear !== toYear;
      const fromMonth = getShortMonthName(from.getMonth()) || "";
      const toMonth = getShortMonthName(to.getMonth()) || "";
      return `${from.getDate()} ${fromMonth}${
        showFromYear ? ` ${fromYear}` : ""
      } - ${to.getDate()} ${toMonth} ${toYear}`;
    }
    return null;
  };

  if (d.redeemType === REDEEMTYPE.online && d.isFlashSale && d.price) {
    keys.push(l.FLASH_SALE_PRICE);
    values.push(_flashSalePriceText(d.price));
  }

  /**
   * Show activation/redemption period if d.showRedemptionPeriod is false
   * d.showRedemptionPeriod == undefined value should be considered true.
   */
  if (d.redemptionFrom && d.redemptionTo && d.showRedemptionPeriod !== false) {
    const periodText = _periodText(d.redemptionFrom, d.redemptionTo);
    if (periodText) {
      keys.push(isReward(d) ? l.REDEMPTION_PERIOD : l.PROMOTION_PERIOD);
      values.push(periodText);
    }
  }
  if (d.website) {
    keys.push(l.WEBSITE);
    values.push(
      <a
        href={
          isApp ? checkAndSetQueryParam(d.website, appQueryParam) : d.website
        }
        target="_blank"
        rel="noopener"
        onClick={(): void =>
          trackLink(
            d,
            isApp ? checkAndSetQueryParam(d.website, appQueryParam) : d.website,
            l.VISIT_LINK
          )
        }
      >
        {l.VISIT_LINK}
      </a>
    );
  }

  if (d.locationLink) {
    const locationText = d.locationText || l.DEFAULT_LOCATION_TEXT;
    keys.push(l.LOCATION);
    values.push(
      <a
        href={
          isApp
            ? checkAndSetQueryParam(d.locationLink, appQueryParam)
            : d.locationLink
        }
        target="_blank"
        rel="noopener"
        onClick={(): void =>
          trackLink(
            d,
            isApp
              ? checkAndSetQueryParam(d.locationLink, appQueryParam)
              : d.locationLink,
            `${locationText} Location`
          )
        }
      >
        {locationText}
      </a>
    );
  }
  if (d.offerCode) {
    keys.push(l.OFFER_CODE);
    values.push(d.offerCode);
  }
  const mobileAttributes: React.ReactNode[][] = keys.map((key, index) => [
    key,
    values[index],
  ]);

  if (keys.length === 0) {
    return null;
  }

  return (
    <>
      <Hidden mdUp>
        <Grid container>
          {mobileAttributes.map((attr, index) => (
            <Grid key={index} item xs={12}>
              <Grid container spacing={1}>
                <Grid sx={{ fontWeight: 700 }} item xs={12}>
                  {attr[0]}
                </Grid>
                <Grid item xs={12}>
                  {attr[1]}
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Hidden>
      <Hidden mdDown>
        <Grid container>
          <Grid item md={3}>
            <Grid container spacing={1}>
              {keys.map((key, index) => (
                <Grid key={index} sx={{ fontWeight: 700 }} item xs={12}>
                  {key}
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item md={9}>
            <Grid container spacing={1}>
              {values.map((value, index) => (
                <Grid key={index} item xs={12}>
                  {value}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Hidden>
    </>
  );
};

Attributes.displayName = "Attributes";

const mapStateToProps = (state: AppState) => ({
  language: state.config.language.details,
});

export default connect(mapStateToProps)(Attributes);
